/**
 * Module dependencies.
 */

import { Error } from 'src/types/forms';
import { FC, ReactElement, forwardRef } from 'react';
import { FormGroup, HelpText, Label } from '../fields-styled-components';
import { units } from '@untile/react-components';
import Checkbox from './checkbox';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import styled from 'styled-components';

/**
 * Export `Option` type.
 */

export type Option = {
  label: string;
  value: string;
};

/**
 * Export `CheckboxGroupProps` interface.
 */

export interface CheckboxGroupProps {
  className?: string;
  disabled?: boolean;
  error?: Error | null | undefined;
  helpText?: string;
  isRequired?: boolean;
  label?: string;
  name: string;
  options: Option[];
}

/**
 * `Options` styled component.
 */

const Options = styled.div`
  display: grid;
  grid-gap: ${units(0.5)};
  padding-top: ${units(0.5)};
`;

/**
 * `CheckboxGroup` component.
 */

const CheckboxGroup: FC<CheckboxGroupProps> = forwardRef<
  any,
  CheckboxGroupProps
>((props: CheckboxGroupProps, ref: any): ReactElement => {
  const {
    className,
    disabled,
    error,
    helpText,
    isRequired,
    label,
    name,
    options,
    ...rest
  } = props;

  const hasError = !isEmpty(error);
  const fieldProps = { ...rest, name, ref };

  return (
    <FormGroup
      className={className}
      disabled={disabled}
    >
      <Label
        hasError={hasError}
        isRequired={isRequired}
      >
        {label}
      </Label>

      <Options>
        {map(options, option => (
          <Checkbox
            {...fieldProps}
            disabled={disabled}
            id={`${name}-${option.value}`}
            key={option.value}
            label={option.label}
            value={option.value}
          />
        ))}
      </Options>

      {hasError && <HelpText hasError>{error?.message}</HelpText>}

      {helpText && !hasError && <HelpText>{helpText}</HelpText>}
    </FormGroup>
  );
});

/**
 * `CheckboxGroup` display name.
 */

CheckboxGroup.displayName = 'CheckboxGroup';

/**
 * Export `CheckboxGroup` component.
 */

export default CheckboxGroup;
